import React, { useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertTriangle, Users } from "lucide-react";

interface ReassignMemberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onReassign: (data: { toTeamId: string; reason: string; notifyMember: boolean }) => Promise<void>;
  member: any;
  currentTeam: { id: string; name: string };
  teams: Array<{ id: string; name: string }>;
}

export function ReassignMemberModal({
  isOpen,
  onClose,
  onReassign,
  member,
  currentTeam,
  teams
}: ReassignMemberModalProps) {
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [reason, setReason] = useState('');
  const [notifyMember, setNotifyMember] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const availableTeams = teams.filter(team => team.id !== currentTeam.id);

  React.useEffect(() => {
    if (!isOpen) {
      setSelectedTeamId('');
      setReason('');
      setNotifyMember(true);
      setIsSubmitting(false);
    }
  }, [isOpen]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedTeamId || !reason.trim()) return;

    setIsSubmitting(true);
    try {
      await onReassign({
        toTeamId: selectedTeamId,
        reason: reason.trim(),
        notifyMember
      });
      onClose();
      setSelectedTeamId('');
      setReason('');
      setNotifyMember(true);
    } catch (error) {
      console.error('Failed to reassign member:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      onClose();
      setSelectedTeamId('');
      setReason('');
      setNotifyMember(true);
    }
  };

  if (!member) return null;

  const selectedTeam = availableTeams.find(team => team.id === selectedTeamId);
  const noTeamsAvailable = availableTeams.length === 0;

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Reassign Team Member
          </DialogTitle>
          <DialogDescription>
            Move {member.user?.fullName || `${member.user?.firstName} ${member.user?.lastName}`} to a different team
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="space-y-4">
            {/* Current Team Info */}
            <div className="p-4 bg-muted rounded-lg">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium">Current Team</p>
                  <p className="text-sm text-muted-foreground">{currentTeam.name}</p>
                </div>
                <div className="text-right">
                  <p className="font-medium">{member.user?.fullName || `${member.user?.firstName} ${member.user?.lastName}`}</p>
                  <p className="text-sm text-muted-foreground">{member.role || 'Member'}</p>
                </div>
              </div>
            </div>

            {/* New Team Selection */}
            <div className="space-y-2">
              <Label htmlFor="new-team">Reassign to Team</Label>
              <Select
                value={selectedTeamId}
                onValueChange={setSelectedTeamId}
                disabled={noTeamsAvailable}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder={noTeamsAvailable ? 'No other teams available' : 'Select a team'} />
                </SelectTrigger>
                <SelectContent>
                  {availableTeams.map((team) => (
                    <SelectItem key={team.id} value={team.id}>
                      {team.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Reason */}
            <div className="space-y-2">
              <Label htmlFor="reason">Reason for Reassignment</Label>
              <Textarea
                id="reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Provide a reason for this reassignment..."
                required
                rows={3}
              />
            </div>

            {/* Notification Option */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="notify"
                checked={notifyMember}
                onCheckedChange={(checked) => setNotifyMember(checked === true)}
              />
              <Label htmlFor="notify" className="text-sm">
                Notify member via email about this reassignment
              </Label>
            </div>

            {/* Warning */}
            {selectedTeam && (
              <div className="flex items-start gap-3 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-yellow-800">Reassignment Impact</p>
                  <p className="text-yellow-700">
                    {member.user?.fullName || `${member.user?.firstName} ${member.user?.lastName}`} will be moved to{' '}
                    <strong>{selectedTeam.name}</strong> and removed from{' '}
                    <strong>{currentTeam.name}</strong>. This action cannot be undone.
                  </p>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={!selectedTeamId || !reason.trim() || isSubmitting}
            >
              {isSubmitting ? 'Reassigning...' : 'Reassign Member'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
